import { Capacitor } from "@capacitor/core";
import { reportClientError } from "@/lib/errorReporter";

/**
 * Firebase Crashlytics — native crash + non-fatal error reporting.
 *
 * Only meaningful on the native apps (Capacitor iOS/Android), where the
 * Firebase Crashlytics SDK is linked via @capacitor-firebase/crashlytics.
 * On the web it is a safe no-op — the plugin has no browser implementation,
 * so every call is dynamically imported and wrapped in catch.
 *
 * Fatal native crashes (a hard iOS/Android crash) are captured automatically
 * by the SDK once it is enabled. What this module adds:
 *   - turning collection on at launch,
 *   - forwarding JavaScript errors (React render crashes, unhandled promise
 *     rejections, window.onerror) as **non-fatal** reports so a white-screen
 *     bug in the webview still shows up in the Crashlytics console, and
 *   - tagging reports with the signed-in user so a crash can be traced to a
 *     member.
 *
 * Like analytics, reporting is fire-and-forget: it must never break a flow.
 */

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function withPlugin(
  fn: (
    plugin: typeof import("@capacitor-firebase/crashlytics").FirebaseCrashlytics,
  ) => Promise<unknown>,
): void {
  if (!isNative()) return;
  import("@capacitor-firebase/crashlytics")
    .then(({ FirebaseCrashlytics }) => fn(FirebaseCrashlytics))
    .catch(() => {
      // Plugin not installed on this platform / Firebase not configured.
    });
}

/** Turn a thrown value into a message + a Crashlytics stack frame list. */
function toStacktrace(error: unknown): {
  message: string;
  stacktrace: { fileName: string }[];
} {
  const err =
    error instanceof Error ? error : new Error(String(error ?? "unknown"));
  const message = err.name ? `${err.name}: ${err.message}` : err.message;
  const stacktrace = (err.stack ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => ({ fileName: line }));
  return { message, stacktrace };
}

let handlersRegistered = false;

/**
 * Enable Crashlytics collection (native) and forward JS errors — as
 * non-fatals to Crashlytics on native, and to report-client-error on every
 * platform. Call once at app start; safe to call more than once.
 */
export function initCrashlytics(): void {
  if (isNative()) withPlugin((c) => c.setEnabled({ enabled: true }));

  // The handlers below are registered on the WEB as well, which they weren't
  // before. Crashlytics is still native-only, but recordError now also posts
  // to report-client-error, and on the web those two listeners are the only
  // thing standing between an uncaught error and total silence.
  if (handlersRegistered || typeof window === "undefined") return;
  handlersRegistered = true;

  window.addEventListener("error", (event) => {
    recordError(event.error ?? event.message, { source: "window.onerror" });
  });
  window.addEventListener("unhandledrejection", (event) => {
    recordError(event.reason, { source: "unhandledrejection" });
  });
}

/**
 * Record a non-fatal error — to Crashlytics on native, and to our own
 * client_errors table everywhere. Used by the app-wide ErrorBoundary and the
 * global window handlers, but can be called anywhere a caught error is worth
 * keeping.
 */
export function recordError(
  error: unknown,
  context: Record<string, string> = {},
): void {
  // Goes to the database on every platform, so the watchdog can email about a
  // spike and the daily digest covers the apps as well as the site.
  reportClientError(error, { source: context.source });

  const { message, stacktrace } = toStacktrace(error);
  // `type` is required by CustomKeyAndValue; context is Record<string,string>,
  // so every value is a string.
  const keysAndValues = Object.entries(context).map(([key, value]) => ({
    key,
    value,
    type: "string" as const,
  }));
  withPlugin((c) =>
    c.recordException({
      message,
      stacktrace,
      ...(keysAndValues.length ? { keysAndValues } : {}),
    }),
  );
}

/** Tie subsequent crash reports to the signed-in member (cleared on sign-out with ""). */
export function setCrashlyticsUser(userId: string): void {
  withPlugin((c) => c.setUserId({ userId }));
}

/** Breadcrumb added to the next crash report — useful for reconstructing the path in. */
export function crashlyticsLog(message: string): void {
  withPlugin((c) => c.log({ message }));
}
